import React, { createContext, useContext, useEffect } from "react";
import { analyticsService } from "./service";
import { AnalyticsEvent } from "./model";
import { useUser } from "../user/user-provider";
import { config } from "../config";

interface AnalyticsContextProps {
  track: (event: AnalyticsEvent, properties?: Record<string, any>) => void;
  trackError: (event: AnalyticsEvent, error: Error) => void;
}

const AnalyticsContext = createContext<AnalyticsContextProps | undefined>(
  undefined,
);

export default function AnalyticsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useUser();

  useEffect(() => {
    if (user) {
      analyticsService.setUser(user);
    } else {
      analyticsService.clearUser();
    }
  }, [user]);

  const track = (event: AnalyticsEvent, properties?: Record<string, any>) => {
    if (config.IS_PROD) {
      analyticsService.track(event, properties);
    }
  };

  const trackError = (event: AnalyticsEvent, error: Error) => {
    analyticsService.trackError(event, error);
  };

  return (
    <AnalyticsContext.Provider value={{ track, trackError }}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};
